import { Button } from "@/components/common/Button";
import { useAirdropValueStore } from "@/modules/airdrop/store/value";
import { postWithdrawAirdrops } from "@/modules/auth/api/deposit/airdrop";
import { useUserStore } from "@/modules/auth/store/user";
import {
  useModalSuccessfulAirdropStore,
} from "@/shared/store/modal";
import { AnalyticsEvents } from "@/shared/utils/analytics/events";
import { logger } from "@/shared/utils/logger";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useChainId } from "wagmi";
import { useNavigate } from "react-router";

// TODO: Always in TON. Should be in BTN, TON, Reinvest
const ClaimAirdrop = () => {
  const { t } = useTranslation();
  const { user, airdrop, airdropBASE, stopPolling, startPolling } =
    useUserStore();
  const [tonConnectUI] = useTonConnectUI();
  const chainId = useChainId();
  const navigate = useNavigate();

  const [isLoadingWithdraw, setIsLoadingWithdraw] = useState(false);

  const { currentToken, net, setCurrentToken } = useAirdropValueStore();

  const { openModal, openModalError } = useModalSuccessfulAirdropStore();

  const disabled = Boolean(
    airdrop.ton <= 0 ||
      (airdrop.minWithdraw && airdrop.ton < airdrop.minWithdraw) ||
      !tonConnectUI?.account?.address
  );

  const disabledBASE = Boolean(
    airdropBASE.token <= 0 ||
      (airdropBASE.minWithdraw &&
        airdropBASE.token < airdropBASE.minWithdraw) ||
      !user?.walletId
  );

  const handleConnect = () => {
    tonConnectUI.openModal();
  };

  const handleConnectEVM = () => {
    navigate("/settings");
  };

  const handleClick = async () => {
    if (isLoadingWithdraw) return;

    try {
      setIsLoadingWithdraw(true);
      stopPolling();

      const result = await postWithdrawAirdrops(
        currentToken as "ton" | "token",
        net === "base" ? "EVM" : "TON",
        net === "base" ? chainId : 0
      );

      if (!result?.withdrawalId && openModalError) {
        setIsLoadingWithdraw(false);
        openModalError();

        return;
      }
      openModal();
    } catch (error) {
      startPolling(chainId || 0);

      logger.error("Failed to withraw airdrops:", error);

      if (openModalError) openModalError();
    } finally {
      setIsLoadingWithdraw(false);
    }
  };

  return net === "ton" ? (
    <div className="flex flex-col gap-5 p-4 border-b border-divider box-content">
      {currentToken === "" && (
        <div className="flex flex-col gap-4">
          <div className="w-full flex items-center justify-between gap-4">
            <Button
              onClick={() => setCurrentToken("token")}
              text="BTN"
              mode="bezeled"
              size="large"
              className="w-full"
            />

            <Button
              onClick={() => setCurrentToken("ton")}
              text="TON"
              mode="bezeled"
              size="large"
              className="w-full"
            />
          </div>
        </div>
      )}

      {currentToken !== "" && (
        <>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setCurrentToken("")}
              icon="arrow-left"
              mode="bezeled"
              size="large"
              className="text-[0.9375rem] !leading-[1.25rem]"
            />
            {disabled && !tonConnectUI.account?.address ? (
              <Button
                onClick={handleConnect}
                text={t("main-page.connect_wallet")}
                mode="bezeled"
                size="large"
                className="w-full"
              />
            ) : (
              <Button
                onClick={handleClick}
                text={`${t("main-page.claim_airdrop")} ${
                  currentToken === "ton" ? "TON" : "BTN"
                }`}
                mode="bezeled"
                size="large"
                className="w-full"
                disabled={disabled}
                loading={isLoadingWithdraw}
                analyticsEvent={AnalyticsEvents.ClaimAirdropPage.Withdraw}
              />
            )}
          </div>

          <p className="text-[0.8125rem] leading-[1rem] text-section-header-text px-4 text-center tracking-tighter">
            {t("airdrop-page.tokens_credited")}
          </p>
        </>
      )}
    </div>
  ) : (
    <div className="flex flex-col gap-5 p-4 border-b border-divider box-content">
      {currentToken === "" && (
        <div className="flex flex-col gap-4">
          <div className="w-full flex items-center justify-between gap-4">
            <Button
              onClick={() => setCurrentToken("token")}
              text="BTN"
              mode="bezeled"
              size="large"
              className="w-full"
            />

          </div>
        </div>
      )}

      {currentToken !== "" && (
        <>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setCurrentToken("")}
              icon="arrow-left"
              mode="bezeled"
              size="large"
              className="text-[0.9375rem] !leading-[1.25rem]"
            />
            {disabledBASE ? (
              <Button
                onClick={handleConnectEVM}
                text={t("main-page.connect_base_wallet")}
                mode="bezeled"
                size="large"
                className="w-full"
                analyticsEvent={
                  AnalyticsEvents.WithdrawRewardsPage.ConnectWallet
                }
              />
            ) : (
              <Button
                onClick={handleClick}
                text={`${t("main-page.claim_airdrop")} BTN`}
                mode="bezeled"
                size="large"
                className="w-full"
                disabled={disabledBASE}
                loading={isLoadingWithdraw}
                analyticsEvent={AnalyticsEvents.ClaimAirdropPage.Withdraw}
              />
            )}
          </div>

          <p className="text-[0.8125rem] leading-[1rem] text-section-header-text px-4 text-center tracking-tighter">
            {net === "base"
              ? "Tokens will be credited to your wallet"
              : t("airdrop-page.tokens_credited")}
          </p>
        </>
      )}
    </div>
  );
};

export default ClaimAirdrop;
